import { anchored } from '../util/viewportAnchor.js';
import { statusPhrase } from '../util/statusPhrase.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatBigNum } from '../util/formatBig.js';
import { flashToast } from '../util/toast.js';
import { BATTLE_STATS, type BattleStat, type TrainingTierDto } from '@rex-mortis/shared';

// "Train Max" uses the player's full current vigor — the server cap is set
// well above any realistic vigorMax, so we don't pre-clip it here.

// Cleanup for the tier-picker's document listeners. Each renderMain re-binds
// them; we tear down the previous binding first so heavy training sessions
// don't stack hundreds of document click/keydown listeners (they were only
// removed on Escape before).
let tierPickerCleanup: (() => void) | null = null;

const STAT_LABEL: Record<BattleStat, string> = {
  strength: 'Strength',
  defense: 'Defense',
  speed: 'Speed',
  dexterity: 'Dexterity',
};

const STAT_FLAVOR: Record<BattleStat, string> = {
  strength: 'Damage of every blow you land.',
  defense: 'Blunting the bite of incoming steel.',
  speed: 'How often the blade finds its mark.',
  dexterity: 'Slipping the strike, striking unseen.',
};

/**
 * Each training yard gets its own colour gradient — a visual fingerprint
 * that escalates from humble earth tones through martial colours, royal
 * regalia, and finally into cosmic / mythic palettes for the endgame.
 * Values are two-stop CSS `linear-gradient` arguments (without the wrapper).
 */
const TIER_GRADIENT: Record<string, string> = {
  tyro_yard:        'linear-gradient(135deg, #c9b48a 0%, #6b5238 100%)',
  sand_pit:         'linear-gradient(135deg, #e2c98a 0%, #8b6a30 100%)',
  pell_yard:        'linear-gradient(135deg, #8a6443 0%, #3a2a1a 100%)',
  sparring_ring:    'linear-gradient(135deg, #a8825a 0%, #4a3008 100%)',
  masters_circle:   'linear-gradient(135deg, #c08a40 0%, #6a3a0e 100%)',
  tournament_lists: 'linear-gradient(135deg, #4a6aa8 0%, #1a2f5a 100%)',
  guild_hall:       'linear-gradient(135deg, #b07238 0%, #5a2e0a 100%)',
  royal_yard:       'linear-gradient(135deg, #3a5aa8 0%, #b58b1c 100%)',
  champions_walk:   'linear-gradient(135deg, #f0d672 0%, #8a6614 100%)',
  gauntlet_run:     'linear-gradient(135deg, #9c2c2c 0%, #3a0808 100%)',
  order_keep:       'linear-gradient(135deg, #8090a0 0%, #2a3a4a 100%)',
  crucible_minor:   'linear-gradient(135deg, #e85a18 0%, #6a1a0a 100%)',
  shadow_yard:      'linear-gradient(135deg, #7a5a8a 0%, #2a1a3a 100%)',
  ironbound_court:  'linear-gradient(135deg, #5a5a5a 0%, #0e0e0e 100%)',
  kings_arena:      'linear-gradient(135deg, #b22c2c 0%, #6a4a0a 100%)',
  titan_floor:      'linear-gradient(135deg, #8a6a3a 0%, #2a1a08 100%)',
  storm_arena:      'linear-gradient(135deg, #5a8acc 0%, #1a2a4a 100%)',
  midnight_yard:    'linear-gradient(135deg, #2a3a7a 0%, #050a1a 100%)',
  godhall_practice: 'linear-gradient(135deg, #fff5e1 0%, #d4a93c 50%, #6a4a08 100%)',
  lichking_yard:    'linear-gradient(135deg, #8aa848 0%, #1a3a1a 100%)',
  starless_court:   'linear-gradient(135deg, #4a1a7a 0%, #050010 100%)',
  eclipse_arena:    'linear-gradient(135deg, #b22c2c 0%, #2a0a3a 50%, #050505 100%)',
  voidwalker_ring:  'linear-gradient(135deg, #3a0a7a 0%, #000005 100%)',
  godhammer_floor:  'linear-gradient(135deg, #e9d27a 0%, #b58b1c 50%, #2a2a3a 100%)',
  the_crucible:     'linear-gradient(135deg, #fff5e1 0%, #d44a1a 30%, #6b1f1f 65%, #050010 100%)',
};

/**
 * Monochrome unicode glyph stamped on each yard's gradient swatch. The U+FE0E
 * variation selector forces text-style rendering on platforms that would
 * otherwise default to a colour emoji.
 */
const TIER_SYMBOL: Record<string, string> = {
  tyro_yard:        '♟︎',  // pawn — humble beginner
  sand_pit:         '❖︎',  // simple diamond
  pell_yard:        '✜︎',  // open cross — wooden pell
  sparring_ring:    '⛨︎',  // shield with cross
  masters_circle:   '✦︎',  // four-point star
  tournament_lists: '♞︎',  // knight — mounted lists
  guild_hall:       '❦︎',  // floral heart — fellowship
  royal_yard:       '♔︎',  // king — the Crown's yard
  champions_walk:   '✶︎',  // six-point star — gilded
  gauntlet_run:     '✠︎',  // maltese cross — no quarter
  order_keep:       '☩︎',  // cross of Jerusalem — martial order
  crucible_minor:   '✸︎',  // heavy eight-pointed — forge spark
  shadow_yard:      '☾︎',  // last-quarter moon
  ironbound_court:  '✚︎',  // heavy greek cross — iron
  kings_arena:      '♛︎',  // queen — capital arena
  titan_floor:      '⛭︎',  // gear — colossal stones
  storm_arena:      '✯︎',  // pinwheel star — thunder
  midnight_yard:    '☽︎',  // crescent moon — starless sky
  godhall_practice: '☼︎',  // sun with rays — divine
  lichking_yard:    '☠︎',  // skull — bone-meal
  starless_court:   '⊗︎',  // circled times — void
  eclipse_arena:    '◍︎',  // circle with vertical fill — eclipse
  voidwalker_ring:  '⛧︎',  // inverted pentagram — esoteric
  godhammer_floor:  '⚒︎',  // hammer + pick — thunder forge
  the_crucible:     '⛤︎',  // pentagram — the final test
};

function gradientFor(slug: string): string {
  return TIER_GRADIENT[slug] ?? 'linear-gradient(135deg, #c9b48a 0%, #6b5238 100%)';
}

function symbolFor(slug: string): string {
  return TIER_SYMBOL[slug] ?? '✦︎';
}

function bannerImageFor(slug: string): string {
  return `/img/training/${slug}.webp`;
}

/**
 * In-world lore for each yard — surfaced when the yard is selected.
 * Short flavour paragraphs (1-2 sentences) that give each tier a distinct
 * voice. Falls back to the catalog blurb if a slug isn't listed here.
 */
const TIER_LORE: Record<string, string> = {
  tyro_yard:        "Where every blade begins. The earth here is packed flat by a thousand pairs of feet — beggars, second sons, sworn squires — all of the realm's hopefuls learning which end of a sword to hold.",
  sand_pit:         "A circle drawn in pale river-sand, deep enough to soften a fall. The first lesson is footwork: how to find balance when the ground itself shifts under thee.",
  pell_yard:        "Six oak posts, gouged half to splinters by years of hammering. The pell does not parry, does not feint, does not surrender — it simply waits for thy mistake.",
  sparring_ring:    "Now the wood gives way to steel. Padded blades, ringed combat, the first taste of an opponent who hits back. Many a champion was forged in this quiet stone hall.",
  masters_circle:   "A small marble dais where greying veterans teach what no manual contains. Here form yields to instinct, and instinct to art.",
  tournament_lists: "The pennanted track where knights ride for glory and gold. To train here is to train in the language of courts and crowds — every charge a spectacle.",
  guild_hall:       "The mercenary's school. Bronze braziers, hung trophies, and instructors who teach killing as a trade rather than an honour. Coin pays better than oaths.",
  royal_yard:       "The crown's own training ground, paved in flagstones polished by generations of guardsmen. Few step here who do not wear a king's livery.",
  champions_walk:   "A gilded colonnade lined with the stone faces of every champion to bear the realm's name. Walk it with reverence — they are watching.",
  gauntlet_run:     "No mercy, no instruction. Swinging beams, falling blades, snarling drillmasters. Those who survive emerge with bruises that will never wholly heal — and reflexes that will save them a thousand times.",
  order_keep:       "Within the white-walled keep, the warrior-monks drill in silent ranks at dawn and dusk. To train here is to learn that discipline is a weapon sharper than any blade.",
  crucible_minor:   "Underground, where the forges roar and the air shimmers with heat. The smiths say the steel learns its temper in fire. So too, they say, do the men who swing it.",
  shadow_yard:      "A courtyard known to few, hidden behind a high wall. Lessons are taught in moonlight, in silence, in motion that leaves no echo on the stone.",
  ironbound_court:  "No banner flies, no colour graces these walls. Only chains, iron, and the weight of armour. Those who train here learn to outlast — for the last one standing is always the victor.",
  kings_arena:      "The vast capital ring, where champions duel under the sovereign's own eye. Thirty thousand mouths roar at every blow. Train here and learn to fight with the world watching.",
  titan_floor:      "Built atop the toppled ruins of giants, where weapons larger than wagons lean against fallen columns. To train among such relics is to remember: there were greater things than men, once.",
  storm_arena:      "A clifftop ring lashed by the open sea. Lessons begin at the height of the tempest, when steel rings against thunder and the gods themselves seem to be sparring overhead.",
  midnight_yard:    "No moon, no torch, no mercy. Train in absolute darkness, and the blade becomes an extension of breath. Those who emerge see clearly even in daylight ever after.",
  godhall_practice: "A cathedral-sized hall where shafts of holy light fall upon the kneeling faithful. Two silent angels stand watch — to bless the worthy, and to weigh the rest.",
  lichking_yard:    "A courtyard of bone and witchfire, where a long-dead king teaches sword-craft to those who would learn from death itself. Every lesson costs a piece of the soul.",
  starless_court:   "A black marble platform suspended in the void, where no sky exists and no horizon waits. Those who train here learn to fight with no point of reference but themselves.",
  eclipse_arena:    "Beneath a perpetual eclipse, where the black sun bleeds fire and time itself runs slow. Every duel feels like an eternity. Every wound, immortal.",
  voidwalker_ring:  "A circle of floating obsidian shards, drifting where physics fails. Lessons here are not about technique but conviction — for the platform answers to the certainty of the one who stands upon it.",
  godhammer_floor:  "High above the cloud-line, beside the resting hammer of a slain god. The very air thrums with old thunder. Those who train here for long enough begin to dream of striking lightning.",
  the_crucible:     "The final test. A bridge of cracked obsidian above an abyss of fire and void. Cross it, sword raised, and become a name the gods themselves remember. Fail, and not even the gods will know thy fate.",
};

function loreFor(slug: string, fallback: string): string {
  return TIER_LORE[slug] ?? fallback;
}

interface FlashState {
  kind: 'success' | 'danger';
  message: string;
}

export async function renderTrainingYard(root: HTMLElement): Promise<void> {
  // Refresh state on entry.
  let tiers: TrainingTierDto[] = [];
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const tr = await api.listTrainingTiers();
    tiers = tr.tiers;
  } catch {
    // tolerated
  }

  let flash: FlashState | null = null;
  // Default selection: highest unlocked tier (rightmost in the array).
  let selectedTier: string = (() => {
    const unlocked = tiers.filter((t) => t.unlocked);
    const last = unlocked[unlocked.length - 1];
    return last ? last.slug : 'tyro_yard';
  })();
  let firstRender = true;

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      // ── Scroll-anchor snapshot ────────────────────────────────────────────
      // innerHTML rebuild + a new flash banner can shift visible content, and
      // some browsers transiently clamp scrollY while the element is empty.
      // Capture the position + page height so we can restore it once the new
      // DOM is in place.
      const prevScrollY = window.scrollY;
      const prevScrollHeight = document.documentElement.scrollHeight;

      const tier = tiers.find((t) => t.slug === selectedTier);
      const unlockedCount = tiers.filter((t) => t.unlocked).length;
      // The unlock rule is: MIN(strength, defense, speed, dexterity) ≥ unlockMinStat.
      // Each yard's progress bar is current min stat / its required min stat.
      const minStatNow = Math.min(char.strength, char.defense, char.speed, char.dexterity);
      const pct = (req: number) => {
        if (req <= 0) return 100;
        return Math.max(0, Math.min(100, (minStatNow / req) * 100));
      };

      // Build the page.
      main.innerHTML = `
        <img class="page-banner" src="/img/banners/training.webp" alt="" />
        <h1>The Training Yard</h1>
        <p class="subtitle" style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Sweat now, bleed less later.
        </p>

        <div id="flash"></div>

        <div class="panel">
          <h2>Resources</h2>
          <dl class="kv">
            <dt>Vigor</dt><dd>${char.vigor} / ${char.vigorMax}</dd>
            <dt>Spirit</dt><dd>${char.spirit} / ${char.spiritMax} <span style="color: var(--c-gold-text); font-weight: 600;">(+${Math.round(100 * Math.log2(1 + char.spirit / 250))}% training)</span></dd>
            <dt>Status</dt><dd>${char.status}</dd>
          </dl>
          <p class="caption" style="margin: 4px 0 0 0; color: var(--c-ink-soft); font-size: 0.88rem; line-height: 1.45;">
            <strong>Spirit Attunement:</strong> the more Spirit you hold, the harder each point of Vigor trains.
            A bigger bonus burns Spirit faster — so bank a full reserve, then unleash it for a powerful run.
          </p>
          ${
            char.status !== 'OKAY'
              ? `<p class="alert danger">You cannot train while ${statusPhrase(char.status)}.</p>`
              : ''
          }
        </div>

        <div class="panel">
          <h2 style="margin-top:0;">Training Yard ${unlockedCount > 0 ? `<span style="font-size:0.8rem; color: var(--c-ink-soft);">— ${unlockedCount}/${tiers.length} unlocked</span>` : ''}</h2>
          <p style="font-style:italic; color: var(--c-ink-soft); margin-top: 0;">
            Higher tiers cost more vigor per session, but reward far more stat. To unlock a yard,
            <strong>every</strong> battle stat (Strength, Defense, Speed, Dexterity) must reach the threshold —
            your <em>lowest</em> stat is what gates you.
          </p>
          <p style="font-size: 0.92rem; color: var(--c-ink-soft); margin: 4px 0 var(--s-3) 0;">
            Your lowest battle stat is currently <strong>${formatStat(minStatNow)}</strong>.
          </p>
          <div class="field">
            <label>Active Yard</label>
            ${tier ? `
              <div class="yard-picker" data-open="false">
                <button class="yard-picker-trigger" type="button" id="yard-trigger" aria-haspopup="listbox" aria-expanded="false">
                  <span class="yard-swatch">
                    <img class="yard-swatch-img" src="${bannerImageFor(tier.slug)}" alt="" loading="lazy" />
                    <span class="yard-tier-num">${tiers.findIndex((t) => t.slug === tier.slug) + 1}<span class="yard-tier-total">/${tiers.length}</span></span>
                  </span>
                  <span class="yard-picker-body">
                    <span class="yard-picker-name">${tier.name}</span>
                    <span class="yard-picker-meta">${tier.gainMultiplier}× gain · ${tier.vigorCostMultiplier}× vigor cost</span>
                  </span>
                  <span class="yard-picker-caret">▾</span>
                </button>
                <div class="yard-picker-menu" role="listbox" aria-labelledby="yard-trigger">
                  ${tiers
                    .map((t, i) => {
                      const p = pct(t.unlockMinStat);
                      return `
                      <button
                        class="yard-option ${t.slug === selectedTier ? 'selected' : ''} ${!t.unlocked ? 'locked' : ''}"
                        type="button"
                        role="option"
                        data-slug="${t.slug}"
                        ${!t.unlocked ? 'disabled' : ''}
                        aria-selected="${t.slug === selectedTier}"
                      >
                        <span class="yard-swatch">
                          <img class="yard-swatch-img" src="${bannerImageFor(t.slug)}" alt="" loading="lazy" />
                          <span class="yard-tier-num">${i + 1}<span class="yard-tier-total">/${tiers.length}</span></span>
                        </span>
                        <span class="yard-option-body">
                          <span class="yard-option-name">${t.name}</span>
                          <span class="yard-option-meta">
                            <span class="gain">${t.gainMultiplier}× gain</span>
                            ${t.unlockMinStat > 0
                              ? `<span class="${t.unlocked ? 'unlock-tag' : 'locked-tag'}">min stat ${formatBigNum(t.unlockMinStat)}</span>`
                              : '<span class="unlock-tag">starting yard</span>'}
                          </span>
                          <span class="yard-progress" title="${formatStat(minStatNow)} / ${formatBigNum(t.unlockMinStat || 1)} min stat">
                            <span class="yard-progress-fill ${t.unlocked ? 'done' : ''}" style="width: ${p.toFixed(2)}%;"></span>
                          </span>
                        </span>
                        ${t.slug === selectedTier ? '<span class="yard-check">✓</span>' : !t.unlocked ? '<span class="yard-lock">⚿</span>' : ''}
                      </button>
                    `;}).join('')}
                </div>
              </div>
            ` : ''}
          </div>
          ${tier ? `
            <figure class="yard-banner-card">
              <img class="yard-banner-img" src="${bannerImageFor(tier.slug)}" alt="${tier.name}" loading="lazy" />
              <figcaption class="yard-banner-caption">
                <div class="yard-banner-name">${tier.name}</div>
                <p class="yard-banner-blurb">"${tier.blurb}"</p>
              </figcaption>
            </figure>
            <blockquote class="yard-lore">${loreFor(tier.slug, tier.blurb)}</blockquote>
            <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2);">
              <span class="pill gold">${tier.gainMultiplier}× gain</span>
              <span class="pill">${tier.vigorCostMultiplier}× vigor cost</span>
              ${tier.unlockMinStat > 0 ? `<span class="pill">Unlock: every stat ≥ ${formatBigNum(tier.unlockMinStat)}</span>` : ''}
            </div>
          ` : ''}
        </div>

        <div id="stats" class="stats-grid"></div>
      `;

      // Custom yard picker — open/close + pick.
      const picker = main.querySelector<HTMLDivElement>('.yard-picker');
      const trigger = main.querySelector<HTMLButtonElement>('#yard-trigger');
      if (picker && trigger) {
        const setOpen = (v: boolean) => {
          picker.setAttribute('data-open', String(v));
          trigger.setAttribute('aria-expanded', String(v));
        };
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          setOpen(picker.getAttribute('data-open') !== 'true');
        });
        picker.querySelectorAll<HTMLButtonElement>('.yard-option').forEach((opt) => {
          opt.addEventListener('click', (e) => {
            e.stopPropagation();
            if (opt.disabled) return;
            const slug = opt.dataset.slug;
            if (!slug) return;
            selectedTier = slug;
            setOpen(false);
            anchored(renderMain);
          });
        });
        // Tear down any listeners left by a previous render before re-binding,
        // so repeated renders can't stack document listeners.
        tierPickerCleanup?.();
        // Click outside to close.
        const onDocClick = (ev: MouseEvent) => {
          if (!picker.contains(ev.target as Node)) setOpen(false);
        };
        const onKey = (ev: KeyboardEvent) => {
          if (ev.key === 'Escape') { setOpen(false); tierPickerCleanup?.(); }
        };
        document.addEventListener('click', onDocClick, { once: false });
        document.addEventListener('keydown', onKey);
        tierPickerCleanup = () => {
          document.removeEventListener('click', onDocClick);
          document.removeEventListener('keydown', onKey);
          tierPickerCleanup = null;
        };
      }

      // Inject styles inline so we don't have to touch the CSS file for one page.
      const style = document.createElement('style');
      style.textContent = `
        /* (Global .pill styles in pages.css now own the look — no override here.) */

        /* ── Custom yard picker ───────────────────────────────────────── */
        .yard-picker {
          position: relative;
          max-width: 560px;
        }
        .yard-picker-trigger {
          display: flex;
          align-items: center;
          gap: var(--s-3);
          width: 100%;
          padding: 8px 12px;
          background: linear-gradient(to bottom, rgba(255, 245, 220, 0.85), rgba(214, 195, 147, 0.6));
          border: 1px solid var(--c-rule-strong);
          border-radius: var(--radius);
          cursor: pointer;
          font: inherit;
          /* The gradient cell stays light-cream in both themes, so pin dark
             ink — var(--c-ink) flips to cream in dark mode and vanished. */
          color: #1c1108;
          text-align: left;
          box-shadow:
            var(--emboss-light),
            0 1px 2px rgba(28, 17, 8, 0.10);
          transition: filter 0.12s ease, box-shadow 0.12s ease;
        }
        .yard-picker-trigger:hover { filter: brightness(1.04); }
        .yard-picker[data-open="true"] .yard-picker-trigger {
          box-shadow:
            var(--emboss-light),
            0 0 0 3px rgba(212, 169, 60, 0.30);
        }
        .yard-swatch {
          flex: 0 0 auto;
          width: 80px;
          height: 40px;
          border-radius: 4px;
          border: 1px solid var(--c-rule-strong);
          box-shadow:
            inset 0 1px 0 rgba(255, 245, 220, 0.35),
            inset 0 -1px 0 rgba(28, 17, 8, 0.30),
            0 1px 1px rgba(28, 17, 8, 0.20);
          overflow: hidden;
          position: relative;
        }
        .yard-swatch-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          image-rendering: -webkit-optimize-contrast;
          transition: filter 0.12s ease;
        }
        /* Locked yards: greyed out so the unlocked ones pop visually. */
        .yard-option.locked .yard-swatch-img {
          filter: grayscale(1) brightness(0.55);
        }
        /* Tier-number badge stamped in the top-left of every swatch. */
        .yard-tier-num {
          position: absolute;
          top: 2px;
          left: 2px;
          padding: 1px 5px;
          background: rgba(20, 12, 4, 0.78);
          color: #fff5e1;
          border: 1px solid rgba(255, 245, 220, 0.25);
          border-radius: 3px;
          font-family: var(--font-numeric);
          font-size: 0.72rem;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0.02em;
          pointer-events: none;
          user-select: none;
          text-shadow: 0 1px 1px rgba(0, 0, 0, 0.6);
        }
        .yard-tier-total {
          opacity: 0.65;
          font-weight: 400;
          margin-left: 1px;
        }
        /* Selected yard: gold ring around the swatch for instant recognition. */
        .yard-option.selected .yard-swatch {
          border-color: var(--c-gold);
          box-shadow:
            inset 0 1px 0 rgba(255, 245, 220, 0.35),
            inset 0 -1px 0 rgba(28, 17, 8, 0.30),
            0 0 0 2px rgba(212, 169, 60, 0.55);
        }
        .yard-picker-body { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
        /* Name + meta sit ON the trigger's light gradient — pin dark ink so
           they survive dark mode (the ink tokens flip to cream there). */
        .yard-picker-name {
          font-family: var(--font-display);
          font-size: 1.1rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          color: #1c1108;
        }
        .yard-picker-meta {
          font-family: var(--font-body);
          font-size: 0.9rem;
          color: rgba(28, 17, 8, 0.7);
        }
        .yard-picker-caret {
          font-size: 1.2rem;
          color: var(--c-rule-strong);
          transition: transform 0.18s ease;
        }
        .yard-picker[data-open="true"] .yard-picker-caret { transform: rotate(180deg); }

        .yard-picker-menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          max-height: 380px;
          overflow-y: auto;
          background: var(--c-parchment);
          background-image: var(--parchment-texture);
          border: 1px solid var(--c-rule-strong);
          border-radius: var(--radius);
          box-shadow:
            0 0 0 1px rgba(212, 169, 60, 0.45),
            0 12px 28px rgba(28, 17, 8, 0.35);
          z-index: 30;
          display: none;
          padding: 4px;
        }
        .yard-picker[data-open="true"] .yard-picker-menu { display: block; }

        .yard-option {
          display: flex;
          align-items: center;
          gap: var(--s-3);
          width: 100%;
          padding: 8px 10px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: var(--radius);
          cursor: pointer;
          font: inherit;
          color: var(--c-ink);
          text-align: left;
          transition: background 0.12s ease, border-color 0.12s ease;
        }
        .yard-option:hover:not(:disabled) {
          background: rgba(212, 169, 60, 0.16);
          border-color: var(--c-gold);
        }
        .yard-option.selected {
          background: rgba(107, 31, 31, 0.10);
          border-color: var(--c-oxblood);
        }
        .yard-option.locked, .yard-option:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .yard-option.locked .yard-swatch { filter: grayscale(60%) brightness(0.85); }
        .yard-option-body { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
        .yard-option-name {
          font-family: var(--font-display);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          color: var(--c-ink);
        }
        .yard-option-meta {
          font-family: var(--font-body);
          font-size: 0.85rem;
          color: var(--c-ink-soft);
          display: flex;
          gap: var(--s-2);
          flex-wrap: wrap;
        }
        .yard-option-meta .gain {
          color: var(--c-oxblood);
          font-weight: 600;
        }
        .yard-option-meta .locked-tag {
          color: var(--c-danger);
          font-style: italic;
        }
        .yard-option-meta .unlock-tag {
          color: var(--c-ink-soft);
          font-style: italic;
        }
        /* Progression bar — how close the player's lowest battle stat is
           to this yard's threshold. Full + golden for already-unlocked. */
        .yard-progress {
          display: block;
          height: 5px;
          margin-top: 4px;
          background: rgba(28, 17, 8, 0.18);
          border: 1px solid var(--c-rule);
          border-radius: 2px;
          overflow: hidden;
          box-shadow: var(--shadow-press);
        }
        .yard-progress-fill {
          display: block;
          height: 100%;
          background: linear-gradient(to bottom, #b22c2c, #6b1f1f);
          transition: width 0.25s ease;
        }
        .yard-progress-fill.done {
          background: linear-gradient(to bottom, var(--c-gold-bright), var(--c-gold));
        }
        .yard-check {
          color: var(--c-oxblood);
          font-size: 1.1rem;
          font-weight: 700;
        }
        .yard-lock { color: var(--c-rule-strong); }

        /* Featured banner showing the active yard — full cinematic image with
           a caption overlay along the bottom edge. */
        .yard-banner-card {
          position: relative;
          margin: var(--s-3) 0 0 0;
          border-radius: var(--radius);
          border: 1px solid var(--c-rule-strong);
          overflow: hidden;
          box-shadow:
            inset 0 1px 0 rgba(255, 245, 220, 0.20),
            0 2px 6px rgba(28, 17, 8, 0.25);
          aspect-ratio: 4 / 1;
        }
        .yard-banner-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          image-rendering: -webkit-optimize-contrast;
        }
        .yard-banner-caption {
          position: absolute;
          left: 0; right: 0; bottom: 0;
          padding: var(--s-3) var(--s-4);
          color: #fff5e1;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.70);
          background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.35) 60%, transparent 100%);
        }
        .yard-banner-name {
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: 1.3rem;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }
        .yard-banner-blurb {
          margin: 4px 0 0 0;
          font-family: var(--font-elegant);
          font-style: italic;
          font-size: 0.95rem;
          opacity: 0.95;
        }

        /* In-world lore for the selected yard — sits below the banner image. */
        .yard-lore {
          margin: var(--s-3) 0 0 0;
          padding: var(--s-2) var(--s-3);
          background: var(--c-parchment);
          border-left: 3px solid var(--c-gold);
          border-radius: 0 var(--radius) var(--radius) 0;
          font-family: var(--font-elegant);
          font-style: italic;
          font-size: 0.98rem;
          line-height: 1.55;
          color: var(--c-ink);
          quotes: '\\201C' '\\201D';
        }
        .yard-lore::before { content: open-quote; margin-right: 2px; color: var(--c-oxblood); font-size: 1.2em; line-height: 0.9; vertical-align: -2px; }
        .yard-lore::after  { content: close-quote; margin-left: 2px; color: var(--c-oxblood); font-size: 1.2em; line-height: 0.9; vertical-align: -2px; }
        @media (max-width: 720px) {
          .yard-banner-card { aspect-ratio: 16 / 9; }
          .yard-banner-name { font-size: 1.05rem; }
          .yard-banner-blurb { font-size: 0.85rem; }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: var(--s-4);
        }
        .stat-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-4);
        }
        .stat-card h3 { margin: 0 0 var(--s-1) 0; }
        .stat-card .value {
          font-family: var(--font-numeric);
          font-size: 1.6rem;
          color: var(--c-oxblood);
          margin-bottom: var(--s-2);
        }
        .stat-card .flavor {
          font-style: italic;
          color: var(--c-ink-soft);
          margin-bottom: var(--s-3);
          font-size: 0.92rem;
        }
        .stat-card .actions {
          display: flex;
          gap: var(--s-2);
          flex-wrap: wrap;
        }
        .stat-card .actions .btn {
          padding: 6px 12px;
          font-size: 0.9rem;
        }
      `;
      main.appendChild(style);

      // Flash message — float it as a toast at the top of the screen (same as
      // the deeds pages) instead of an inline banner, so it doesn't shove the
      // controls down or force a scroll. Deduped by object ref, so re-renders
      // won't re-show it.
      if (flash) flashToast(flash);

      const grid = main.querySelector<HTMLDivElement>('#stats')!;

      const canTrain = char.status === 'OKAY' && char.vigor > 0;
      const presets = [1, 5, 25];

      for (const stat of BATTLE_STATS) {
        const card = document.createElement('div');
        card.className = 'stat-card';

        const value = char[stat];

        const maxAffordable = char.vigor;

        card.innerHTML = `
          <h3>${STAT_LABEL[stat]}</h3>
          <div class="value">${formatStat(value)}</div>
          <p class="flavor">${STAT_FLAVOR[stat]}</p>
          <div class="actions">
            ${presets
              .map(
                (n) => `
                  <button class="btn ghost" data-train="${stat}" data-amount="${n}"
                    ${!canTrain || char.vigor < n ? 'disabled' : ''}>
                    Train ${n}
                  </button>`,
              )
              .join('')}
            <button class="btn" data-train="${stat}" data-amount="${maxAffordable}"
              ${!canTrain || maxAffordable < 1 ? 'disabled' : ''}>
              Train Max (${maxAffordable})
            </button>
          </div>
        `;
        grid.append(card);
      }

      // Wire up clicks.
      grid.querySelectorAll<HTMLButtonElement>('button[data-train]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const stat = btn.dataset.train as BattleStat;
          const amount = Number(btn.dataset.amount);
          if (!stat || !Number.isFinite(amount) || amount < 1) return;

          // Disable all train buttons until the round-trip completes.
          const allBtns = grid.querySelectorAll<HTMLButtonElement>('button[data-train]');
          allBtns.forEach((b) => (b.disabled = true));

          try {
            const result = await api.train({ stat, vigor: amount, tier: selectedTier });
            store.set({ character: result.character });
            // Refresh tier unlock state — stats may have crossed a threshold.
            try { tiers = (await api.listTrainingTiers()).tiers; } catch { /* ignore */ }

            const parts = [
              `${STAT_LABEL[stat]} +${formatStat(result.gain)} (spent ${result.vigorSpent} vigor).`,
              result.xpGained > 0 ? `+${formatBigNum(result.xpGained)} XP.` : null,
              result.levelsGained > 0
                ? `🏆 Level up — you are now level ${result.character.level}!`
                : null,
              // The moment the bar zeroes is the most dangerous moment of a new
              // player's session — point at the parallel track instead of a wall.
              result.character.vigor < 1
                ? 'Vigor is spent — but Resolve remains. The crooked road and the honest path both pay.'
                : null,
            ].filter(Boolean);

            flash = {
              kind: 'success',
              message: parts.join(' '),
            };
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Training failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // ── Scroll-anchor restore ────────────────────────────────────────────
      // Skip on the very first render — there's nothing to preserve. On
      // subsequent renders, restore scrollY adjusted by any page-height delta
      // (e.g. a tier unlocking) so the visible content doesn't jump relative to
      // the viewport.
      if (firstRender) {
        firstRender = false;
      } else if (prevScrollY > 0) {
        requestAnimationFrame(() => {
          const newScrollHeight = document.documentElement.scrollHeight;
          const delta = newScrollHeight - prevScrollHeight;
          window.scrollTo(0, prevScrollY + delta);
        });
      }
    };

    anchored(renderMain);
  });
}

function formatStat(n: number): string {
  // Big numbers get the suffix treatment (1.2T, 4.5Qa, ...); smaller values
  // stay in comma-separated form for precision-readable mid-game numbers.
  if (Math.abs(n) >= 1_000_000) return formatBigNum(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
